import { EventEmitter } from 'events'
import Dispatcher from '../Dispatcher'
import BoardStore from '../boards/BoardStore'
import SwimlaneNotFoundError from './errors/SwimlaneNotFoundError'
import {
    ADD_SWIMLANE,
    DELETE_SWIMLANE,
    isSwimlaneAction,
    isSwimlaneMoveAction,
    MOVE_SWIMLANE,
    UPDATE_SWIMLANE,
} from './SwimlaneActions'
import { ITask } from '../../components/task/Task'
import { IBaseMoveError } from '../errors/BaseMoveError'
import {
    SwimlaneMoveError,
    SwimlaneMoveErrorTypes,
} from './errors/SwimlaneMoveError'
import BoardNotFoundError from '../boards/errors/BoardNotFoundError'

/**
 * Represents the raw properties that make up a swimlane
 * without considering child components
 */
export interface ISwimlaneBase {
    id: number
    order: number
    name: string
}

/**
 * Data which represents a swimlane
 */
export interface ISwimlane extends ISwimlaneBase {
    tasks: ITask[]
}

/** Events associated with CRUD operations for swimlanes */
export type CRUDEventTypes =
    | EventsEnum.ADD
    | EventsEnum.EDIT
    | EventsEnum.DELETE
    | EventsEnum.MOVE

/**
 * Type guard for ISwimlaneBase.
 * If value is a ISwimlaneBase then true else false. All code after invocation will be considered
 * ISwimlaneBase if true.
 * @param value Item to check if is ISwimlaneBase
 */
function isISwimlaneBase(value: any | ISwimlaneBase): value is ISwimlaneBase {
    const swimlane = value as ISwimlane
    return !(swimlane.id === undefined || !swimlane.name)
}

/** Represents all possible event values for the swimlane store */
enum EventsEnum {
    ADD = 'SWIMLANESTORE_ADD',
    EDIT = 'SWIMLANESTORE_EDIT',
    DELETE = 'SWIMLANESTORE_DELTE',
    MOVE = 'SWIMLANESTORE_MOVE',
}

/**
 * Store for all swimlanes in the application.
 * Represents an aggregate as all swimlanes belong to a board.
 * As such this implementation wraps the board store.
 */
class SwimlaneStore extends EventEmitter {
    private _nextId = 0

    private _dispatchToken: string

    /**
     * Exposes the dispatch token for the task store
     */
    get DispatchToken() {
        return this._dispatchToken
    }

    /**
     * Computes the initial Total value once the board
     * store has loaded.
     */
    private getInitialTotal(): number {
        const ids = BoardStore.getOrderedBoardsIds()
        if (ids.length === 0) return 0
        const swimlanes = ids
            .map((id) => BoardStore.getBoard(id))
            .flatMap((board) => board.swimlanes)
        if (swimlanes.length === 0) return 0
        return (
            swimlanes.reduce((previous, current) =>
                previous.id > current.id ? previous : current
            ).id + 1
        )
    }

    constructor() {
        super()
        BoardStore.addStateChangeListener(() => {
            if (BoardStore.state !== 'LOADED') return
            this._nextId = this.getInitialTotal()
            return
        })
        this._dispatchToken = Dispatcher.register((action) => {
            // typeguard ensures only IActionPayload<ISwimlanePayload> types get to the switch
            if (isSwimlaneAction(action)) {
                switch (action.actionType) {
                    case ADD_SWIMLANE:
                        this.addSwimlane(
                            action.payload.boardId,
                            action.payload.swimlane
                        )
                        break
                    case UPDATE_SWIMLANE:
                        this.updateSwimlane(
                            action.payload.boardId,
                            action.payload.swimlane
                        )
                        break
                    case DELETE_SWIMLANE:
                        this.deleteSwimlane(
                            action.payload.boardId,
                            action.payload.swimlane
                        )
                        break
                    default:
                    // do nothing
                }
            } else if (isSwimlaneMoveAction(action)) {
                if (action.actionType === MOVE_SWIMLANE) {
                    const { from, to } = action.payload
                    this.moveSwimlane(
                        from.boardId,
                        from.swimlaneId,
                        to.boardId,
                        to.swimlaneId
                    )
                }
            }
        })
    }

    /**
     * Adds a swimlane event listener
     * @param event Event to listen to
     * @param callback Callback to invoke when event raised
     */
    addSwimlaneStoreListener(
        event: EventsEnum,
        callback: (boardId: number, swimlaneId: number) => void
    ) {
        this.on(event, callback)
    }

    /**
     * Removes a swimlane event listener
     * @param event Event to unsubscribe to
     * @param callback Callback to unsubscribe from the event
     */
    removeSwimlaneStoreListener(
        event: EventsEnum,
        callback: (boardId: number, swimlaneId: number) => void
    ) {
        this.removeListener(event, callback)
    }

    /**
     * Adds a swimlane to a board in the store
     * @param boardId Board to add swimlane to
     * @param swimlane Swimlane to add to board
     */
    addSwimlane(boardId: number, swimlane: ISwimlaneBase) {
        // find the board in the board store
        const _board = BoardStore.getBoard(boardId)
        // set the swimlane id
        swimlane.id = this._nextId
        this._nextId += 1
        // set the order
        swimlane.order = _board.swimlanes.length
        // add swimlane to the board
        _board.swimlanes = [..._board.swimlanes, { ...swimlane, tasks: [] }]
        BoardStore.setBoard(_board)
        this.emitCRUDEvent(EventsEnum.ADD, _board.id, swimlane.id)
    }

    /**
     * Updates an existing swimlane and raises edit event, doesn't update order
     * @param boardId Id of the board this swimlane belongs to
     * @param swimlane Swimlane to update
     */
    updateSwimlane(boardId: number, swimlane: ISwimlaneBase) {
        const _swimlane = this.getSwimlane(boardId, swimlane.id)
        const { order, ..._update } = swimlane
        this.setSwimlane(boardId, { ..._swimlane, ..._update })
        this.emitCRUDEvent(EventsEnum.EDIT, boardId, swimlane.id)
    }

    /**
     * Deletes a swimlane from the store / board
     * @param boardId Id of the board this swimlane belongs to
     * @param swimlane Swimlane to delete
     */
    deleteSwimlane(boardId: number, swimlane: ISwimlaneBase) {
        // find the board with this swimlane
        const _board = BoardStore.getBoard(boardId)
        // ensure swimlane is on board
        const _swimlane = this.getSwimlane(boardId, swimlane.id)
        // delete swimlane
        const swimlaneIndex = _board.swimlanes.findIndex(
            (x) => x.id === _swimlane.id
        )
        _board.swimlanes = [
            ..._board.swimlanes.slice(0, swimlaneIndex),
            ..._board.swimlanes.slice(swimlaneIndex + 1),
        ]
        // update order by subtracting 1 from every item after the one deleted
        _board.swimlanes
            .sort((a, b) => a.order - b.order)
            .slice(_swimlane.order)
            .forEach((x) => (x.order -= 1))
        BoardStore.setBoard(_board)
        this.emitCRUDEvent(EventsEnum.DELETE, _board.id, _swimlane.id)
    }

    /**
     * Moves a swimlanes position within a board.
     * Does not support moving a swimlane between boards! just position within a board.
     *
     * Attempting to move a swimlane between boards will result in an InvalidSwimlaneMoveError
     * @param fromBoardId board swimlane to move belongs to
     * @param swimlaneId swimlane to move
     * @param toBoardId board to move the swimlane to
     * @param toSwimlaneId position in target board relative to this swimlane
     * @throws {InvalidSwimlaneMoveError} if fromBoardId and toBoardId don't match
     */
    moveSwimlane(
        fromBoardId: number,
        swimlaneId: number,
        toBoardId: number,
        toSwimlaneId: number
    ) {
        const errors: IBaseMoveError<
            SwimlaneMoveErrorTypes,
            BoardNotFoundError | SwimlaneNotFoundError
        >[] = []
        let fromSwimlane: ISwimlane = {} as ISwimlane
        try {
            fromSwimlane = this.getSwimlane(fromBoardId, swimlaneId)
        } catch (error) {
            if (error instanceof BoardNotFoundError)
                errors.push({
                    errorType: SwimlaneMoveErrorTypes.FromBoard,
                    error,
                })
            else if (error instanceof SwimlaneNotFoundError)
                errors.push({
                    errorType: SwimlaneMoveErrorTypes.FromSwimlane,
                    error,
                })
            else throw error
        }
        let insertAt: number = -1
        try {
            insertAt = this.getSwimlane(toBoardId, toSwimlaneId).order
        } catch (error) {
            if (error instanceof BoardNotFoundError)
                errors.push({
                    errorType: SwimlaneMoveErrorTypes.ToBoard,
                    error,
                })
            else if (error instanceof SwimlaneNotFoundError)
                errors.push({
                    errorType: SwimlaneMoveErrorTypes.ToSwimlane,
                    error,
                })
            else throw error
        }
        // if any errors then throw new task move error
        if (errors.length !== 0) throw new SwimlaneMoveError(errors)
        // beyond this point both swimlane and destination will have been validatd

        // get the destination board
        let toBoard = BoardStore.getBoard(toBoardId)

        // is a move
        //
        // if from.order is smaller than index at then (moving right):
        // Loop from: from.order + 1 to indexAt + 1, for each iteration subtract 1 from order.
        // e.g. from.order = 1 and indexAt = 6 => loop from 2 to 7, take 1 from order of items
        //
        // if indexAt is smaller than from.order then (moving left):
        // Loop from: indexAt to from.order, for each iteration add 1 to order
        // e.g. indexAt = 1 and from.order = 6 => loop from 1 to 6, add 1 to order of items
        const [smallest, largest, amount] =
            fromSwimlane.order < insertAt
                ? [fromSwimlane.order + 1, insertAt + 1, -1]
                : [insertAt, fromSwimlane.order, 1]
        for (let index = smallest; index < largest; index++)
            toBoard.swimlanes[index].order += amount
        // set the order on the swimlane being moved to the desired position
        const _fromSwimlane = toBoard.swimlanes.find(
            (swimlane) => swimlane.id === swimlaneId
        ) as ISwimlane
        _fromSwimlane.order = insertAt
        // sort the array into order
        toBoard.swimlanes.sort((a, b) => a.order - b.order)

        // update the swimlane
        BoardStore.setBoard(toBoard)
        // emit insert
        this.emitCRUDEvent(EventsEnum.MOVE, toBoardId, swimlaneId)
    }

    /**
     * Raises event to say the store has changed
     * @param eventType Type of event to raise
     * @param boardId ID of the board this swimlane belongs to
     * @param id id of the swimlane
     */
    emitCRUDEvent(eventType: EventsEnum, boardId: number, id: number) {
        this.emit(eventType, boardId, id)
    }

    /**
     * Gets a swimlane from a board and returns it
     * @param boardId ID of the board the swimlane belongs to
     * @param swimlaneId ID of the swimlane to fetch
     */
    getSwimlane(boardId: number, swimlaneId: number): ISwimlane {
        const board = BoardStore.getBoard(boardId)
        const swimlane = board.swimlanes.find((x) => x.id === swimlaneId)
        if (!swimlane) throw new SwimlaneNotFoundError(boardId, swimlaneId)
        return { ...swimlane }
    }

    /**
     * Sets a swimlane on a board
     * @param boardId Board swimlane belongs to
     * @param swimlane Swimlane to set on board
     */
    setSwimlane(boardId: number, swimlane: ISwimlane) {
        const _board = BoardStore.getBoard(boardId)
        // ensure swimlane is on board
        if (!_board.swimlanes.find((x) => x.id === swimlane.id))
            throw new SwimlaneNotFoundError(boardId, swimlane.id)
        // update the swimlane in the board
        const swimlaneIndex = _board.swimlanes.findIndex(
            (x) => x.id === swimlane.id
        )
        _board.swimlanes = [
            ..._board.swimlanes.slice(0, swimlaneIndex),
            ..._board.swimlanes.slice(swimlaneIndex + 1),
            swimlane,
        ]
        // preserve order
        _board.swimlanes.sort((a, b) => a.order - b.order)
        BoardStore.setBoard(_board)
    }
}

const swimlaneStore = new SwimlaneStore()
export { swimlaneStore as default, isISwimlaneBase, EventsEnum }
